import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import { axiosInstance } from "@/lib/api";
import { useRealtimeEvent } from "@/lib/socket";
import { useWorkspaceStore } from "@/stores/workspaceStore";

export type NotificationItem = {
  id: string;
  kind: string;
  brandId: string;
  brandName: string;
  slotId: string | null;
  /** The post the line is about ("Fatafeat · Tracing the Bedouin roots of…"), and its month to open it in. */
  slotTitle?: string | null;
  planId?: string | null;
  planYear?: number | null;
  planMonth?: number | null;
  actorName: string;
  excerpt: string;
  createdAt: string;
  readAt: string | null;
};

type NotificationPage = {
  items: NotificationItem[];
  nextCursor: string | null;
};

const PAGE_SIZE = 30;

export const notificationListKey = (organizationId: string) =>
  ["notifications", organizationId] as const;

export const notificationUnreadKey = (organizationId: string) =>
  ["notifications", organizationId, "unread"] as const;

function patchItems(
  data: InfiniteData<NotificationPage> | undefined,
  edit: (item: NotificationItem) => NotificationItem,
): InfiniteData<NotificationPage> | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({ ...page, items: page.items.map(edit) })),
  };
}

/** Unread count for the rail. Refetches when a `notification.created` event arrives for this organization. */
export function useUnreadNotificationCount(organizationId: string | null): number {
  const queryClient = useQueryClient();

  useRealtimeEvent((event) => {
    if (event.type !== "notification.created") return;
    if (!organizationId || event.data.organizationId !== organizationId) return;
    void queryClient.invalidateQueries({ queryKey: ["notifications", organizationId] });
  });

  const query = useQuery({
    queryKey: organizationId ? notificationUnreadKey(organizationId) : ["notifications", "none", "unread"],
    queryFn: async () => {
      const res = await axiosInstance.get<{ count: number }>("/notifications/unread-count", {
        params: { organizationId },
      });
      return res.data.count;
    },
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  });

  return query.data ?? 0;
}

export function useNotificationList(organizationId: string | null, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: organizationId ? notificationListKey(organizationId) : ["notifications", "none"],
    queryFn: async ({ pageParam }) => {
      const res = await axiosInstance.get<NotificationPage>("/notifications", {
        params: {
          organizationId,
          limit: PAGE_SIZE,
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      });
      return res.data;
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: enabled && !!organizationId,
  });
}

export function useNotificationReads(organizationId: string | null) {
  const queryClient = useQueryClient();

  const markRead = useMutation({
    mutationFn: async (notificationId: string) => {
      await axiosInstance.post(`/notifications/${notificationId}/read`);
    },
    onMutate: async (notificationId) => {
      if (!organizationId) return;
      await queryClient.cancelQueries({ queryKey: ["notifications", organizationId] });
      const now = new Date().toISOString();
      queryClient.setQueryData<InfiniteData<NotificationPage>>(notificationListKey(organizationId), (data) =>
        patchItems(data, (item) =>
          item.id === notificationId && !item.readAt ? { ...item, readAt: now } : item,
        ),
      );
      queryClient.setQueryData<number>(notificationUnreadKey(organizationId), (count) =>
        count == null ? count : Math.max(0, count - 1),
      );
    },
    onError: () => {
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: ["notifications", organizationId] });
    },
  });

  const markAllRead = useMutation({
    mutationFn: async () => {
      await axiosInstance.post("/notifications/read-all", { organizationId });
    },
    onMutate: async () => {
      if (!organizationId) return;
      await queryClient.cancelQueries({ queryKey: ["notifications", organizationId] });
      const now = new Date().toISOString();
      queryClient.setQueryData<InfiniteData<NotificationPage>>(notificationListKey(organizationId), (data) =>
        patchItems(data, (item) => (item.readAt ? item : { ...item, readAt: now })),
      );
      queryClient.setQueryData<number>(notificationUnreadKey(organizationId), 0);
    },
    onError: () => {
      if (!organizationId) return;
      void queryClient.invalidateQueries({ queryKey: ["notifications", organizationId] });
    },
  });

  return { markRead, markAllRead };
}

export function useNotificationOrganizationId(): string | null {
  return useWorkspaceStore((s) => s.selectedOrganizationId);
}
